/*========================================================================
 * $Id$
 *
 * Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2013
 *
 * Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
 * Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
 * Urheber festgelegten Stelle in der Funktion des Computerprogramms
 * angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
 * Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
 * Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
 * =========================================================================*/


define (["underscore", "backbone", "utils/jsonpath", "utils/trace"],
    function(_, Backbone, JSONPath, Trace) {

/*
 *  A readonly model which contains the state of a javascript object (e.g. JSON object)
 *  and is accessible like a tree (via submodels). 
 * 
 *  Readonly: 
 *    - Single Properties cannot be updated (no set)
 *    - State Updates can only happen on 
 *          - reset: complettly setting a new state
 *          - update: merging the current state with an update
 *    - Properties are accessible via get(...) or via submodels.
 * 
 * 
 *  State changes are handled via 'load(...)' which handles:
 *    - parsing of the input object (if needed)
 *    - merging / overwriting state
 *    - triggering notifcations 
 * 
 *  Triggers a change event when the tree changes.
 */

var ReadOnlyTreeModelNode = function(treeModel, path, attributes, options) {
  this.treeModel = treeModel;  // of type ReadonlyTreeRoot

  this.path = path;
  this.pathElts  = JSONPath.parsePath(this.path);
  this.canoncialPath = JSONPath.stringifyPath(this.pathElts);
  this.attributes = JSONPath.get(attributes, this.pathElts);
  this.listenTo(this.treeModel, "change", this._onTreeChange, this);
  this._onTreeInvalidate(this.treeModel);
  this.listenTo(this.treeModel, "invalidate", this._onTreeInvalidate, this);
  this.listenTo(this.treeModel, "reset", this._onTreeReset, this);
}

_.extend(ReadOnlyTreeModelNode.prototype, Backbone.Events, {

  submodel: function(path) {
      return new ReadOnlyTreeModelNode(
        this.treeModel,
        path,
        this.attributes
      );
  },
  
  _onTreeInvalidate: function(treeModel, newAttributes) {
    newAttributes = JSONPath.get(this.treeModel.attributes, this.pathElts);
    this._attributesChanged = this._attributesChanged || !_.isEqual(this.attributes, newAttributes);
    this.attributes = newAttributes;
  },
  
  _onTreeChange: function(treeModel, options) {
    if (this._attributesChanged) {
      delete this._attributesChanged;
      this.trigger("change", this, options);
    }
  },
  
  _onTreeReset: function(treeModel, options) {
	 this.trigger("reset", this, options); 
  },

  get: function(attr) {
    if (attr === undefined) return this.attributes;
    if (attr === "computing") {
      return this.treeModel.get("computing");
    }
    if (!this.attributes) return (void 0);

    var result = this.attributes[attr];
    if (result === undefined) {
      result = JSONPath.get(this.attributes, attr);
    }
    return result;
  },

  submodel: function(path, options) {
    if (!_.isArray(path)) {
      path = JSONPath.parsePath(path);
    }
    path = this.pathElts.concat(path);
    return this.treeModel.submodel(path, options);
  },

  load: function(attributes, options) {
    var options = options || {};
    options = _.extend(options, {path: this.pathElts});
    return this.treeModel.load(attributes, options);
  },

  // **dispose** stops this model from being referenced for notifications
  //  the rest is then just to make sure that the reference is null
  dispose: function() {
    this.stopListening(this.treeModel);
  },

  previous: function(attr) {
    if (attr == null || !this._previousAttributes) return null;
    return this._previousAttributes[attr];
  }


});


var ReadOnlyTreeRoot = function(attributes, options) {
  options = options || {};
  var attrs = attributes || {};
  this.cid = _.uniqueId('c');
  this.attributes = {};

  this.loadTransformers = [];

  if (this.initialize) this.initialize(options);

  this._initializeAttributes(attrs, options);
}

_.extend(ReadOnlyTreeRoot.prototype, Backbone.Events, {

  idAttribute: "id",

  submodel: function(path, options) {
      // submodels are special in that they will be notified if this
      // model changes.
      return new ReadOnlyTreeModelNode(this, path, this.attributes, options);
  },

  // **parse** converts a response into the hash of attributes to be `set` on
  // the model. The default implementation is just to pass the response along.
  parse: function(resp) {
      return resp;
  },

  _initializeAttributes: function(attributes, options) {
    options = options || {};
    attributes = attributes || {};
    
    if (!options.path) options.path = [];
    if (_.isArray(options.path)) options.path = JSONPath.parsePath(options.path);
    
    if (options.path.length == 0) {
      if (this.idAttribute in attributes) {
        this.id = attributes.id;
      }
    }
    else if (options.path.length > 0 ) {
      this.attributes = JSONPath.clonePath(this.attributes, options.path);
    }

    this._updateAttributes(attributes, options);
  },

  /**
   * Reinitializes the tree from attributes
   * 
   * @param attributes - the new content of the tree (or of the submodule - depending if options.path is set)
   * @param options - can be used to define a path inside the tree and to specify the new attributes should be parsed or merged
   * @returns a promise which will be resolved with the new (merged/parsed) attributes 
   */
  load: function(attributes, options) {
    options = options || {};
    var atts = attributes || {};
    const that = this;
    
    if (!options.path) options.path = [];
    if (_.isArray(options.path)) options.path = JSONPath.parsePath(options.path);
    
    if (options.path.length == 0) {
      if (this.idAttribute in atts) {
        this.id = atts.id;
      }
    }
    else if (options.path.length > 0 ) {
      this.attributes = JSONPath.clonePath(this.attributes, options.path);
    }

    var _updateAndMergeExistingAttributes = function (currentAttributes) {
      var attributes = currentAttributes;

      that.trigger("load", that, attributes, options);

      if (options.merge) {
        attributes = that._mergeAttributes(attributes, options);
      }

      that._updateAttributes(attributes, options);
      that._triggerAttributeChangeEvents(attributes, options);
      
      return attributes;
    }

    var attributesPromise = Promise.resolve(atts);

    if (options.parse) {
      attributesPromise = this._parseAttributesAsync(atts, options);
    }

    return attributesPromise.then(function(currentAttributes) {
      return _updateAndMergeExistingAttributes(currentAttributes);
    });
  },

  _parseAttributesAsync: function (attributes, options) {
    const traceContext = options.traceContext;

    const outputParseLeave = Trace.events.outputParse.enter(traceContext && traceContext.context);
    options.traceContext = outputParseLeave;
    
    var attributesParsingPromise;

    if (this.parseAsync) {
      attributesParsingPromise = this.parseAsync(attributes, options);
    } else {
      attributesParsingPromise = Promise.resolve(this.parse(attributes, options));
    } 

    return attributesParsingPromise.then(function(parsedAttribtues) {
      outputParseLeave.leave();
      options.traceContext = traceContext;
      return parsedAttribtues;
    })
  },

  _updateAttributes: function (attributes, options) {
    if (options.path.length) {
      JSONPath.set(this.attributes, options.path, attributes);
    }
    else {
      this.attributes = attributes;
    }
  },

  _mergeAttributes: function (attributes, options) {
    const traceContext = options.traceContext;

    var leave = Trace.events.outputParse.enter(traceContext && traceContext.context);
    options.traceContext = leave;

    var parent = this.attributes;
    var existingAtts;

    if (options.path && options.path.length > 0) {
      var last = options.path.length - 1;
      parent = JSONPath.get(this.attributes, options.path, last);
      existingAtts = JSONPath.get(parent, [options.path[last]]);
      options = _.extend(options, {parent: parent});
    }
    else {
      existingAtts = this.attributes;
    }

    // handle computing separatly
    var computing = typeof attributes.computing !== "undefined" ? attributes.computing : 
                    (typeof existingAtts !== "undefined" ? existingAtts.computing : void 0);
    
    attributes = this.merge(existingAtts, attributes, options);
 
    if (computing != null) attributes.computing = computing;

    leave.leave();
    options.traceContext = traceContext;

    return attributes;
  },

  _triggerAttributeChangeEvents: function(attributes, options) {
    this.trigger("invalidate", this, this.attributes, options);

    if (!options.silent) {
      if (options.path.length > 0) {
        var root = JSONPath.get(this.attributes, [options.path[0]]);
        this.trigger("change:" + JSONPath.stringifyPath(options.path[0]), this, root , options);
      }

      var that = this;

      _.each(_.keys(attributes), function(key) {
        that.trigger("change:" + JSONPath.stringifyPath(options.path.concat([key])), that, attributes[key], options);
      });
      
      this.trigger("change", this, options);
    }
  },

  // TODO: Object.assign semantic ; https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
  merge: function(lhs, rhs) {

    if (lhs == null) return rhs;
    if (rhs == null) return lhs;
    if (!_.isObject(lhs) || !_.isObject(rhs)) {
      return rhs; // new wins
    }
    
    var keys = _.keys(rhs);
    var merged = _.clone(lhs);
    for (var k = 0 ; k < keys.length; ++k) {
      var key = keys[k];
      if (merged[key] == null)  {
        merged[key] = rhs[key];
      }
      else {
        merged[key] = this.merge(merged[key], rhs[key]);
      }
    }
    
    return merged;
  },

  // TODO: reset, update? vs load, merge
  // **reset** reinitializes the tree from attributes, parses first
  reset: function(attributes, options) {
    var that = this;

    options = options || {};
    attributes = _.extend({}, attributes, options.additionalAttributes);
    if (!options.silent) {
      this.trigger("before:reset", this, this.attributes, options);
    }

    return this.load(attributes, options)
      .then(function(result) {
        if (!options.silent) {
          that.trigger("reset", that, that.attributes, options);
        }

        return result;
      });
  },

  //**update** merges the attributes with the current attributes
  update: function(attributes, options) {
    options = options || {};

    attributes = _.extend({}, attributes, options.additionalAttributes);
    if (!options.merge) {
      options.merge = true;
    }

    return this.load(attributes, options);
  },

  /// **get** returns the value of an attribute.
  get: function(attr, options) {
    if (attr === undefined) return this.attributes;
    options = options || {} ;
    
    var result;
    if (!options.path) {
      result = this.attributes[attr];      
    }
    
    if (result !== undefined) {
      return result;
    }
    
    var path = options.path || [];
    path = JSONPath.parsePath(path);
    path = path.concat(JSONPath.parsePath(attr));
        
    return JSONPath.get(this.attributes, path);    
  },

  previous: function(attr) {
    if (attr == null || !this._previousAttributes) return null;
    return this._previousAttributes[attr];
  },

  dispose: function() {

  }

});

ReadOnlyTreeRoot.extend = Backbone.Model.extend;
return ReadOnlyTreeRoot;
});
